import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import Footer from "../../Layout/Footer";
import ScrollTopOnMount from "../../Layout/ScrollTopOnMount";
import { CommonPageData } from "../../../styles/common.style";
import BodyViewer from "./components/BodyViewer";
import PainArea from "./components/PainArea";
import PainConfirmation from "./components/PainConfirmation";
import front_side_1 from "../../images/front_side/1.jpg";
import back_side_1 from "../../images/back_side/1.jpg";
import notFound from "../../images/not-found.png";
import FadeLoader from "react-spinners/FadeLoader";

// actions (keep names as in your codebase)
import {
  createIntakeFormQuestion,
  getUserIntakeFormQuestions,
  getUserIntakeCircuit,
  getIntakeFormQuestions,
} from "../../../actions/user_assessment";
import { getPainCategories } from "../../../actions/user_program";
import useGetQuestionInfo from "../../../hooks/useGetQuestionInfo";
import HeaderLogo from "../../common/HeaderLogo";

const VIEW = {
  QUESTIONS: "questions",
  BODY: "body",
  PAIN: "pain",
  SUB_QUESTIONS: "sub_questions",
  PAIN_CONFIRMATION: "pain_confirmation",
};

// Reusable Full-Screen Loader Component
const FullScreenLoader = ({
  isVisible,
  message = "Loading...",
  showOverlay = true,
  backgroundColor = "rgba(0, 0, 0, 0.5)",
}) => {
  if (!isVisible) return null;

  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: showOverlay ? backgroundColor : "transparent",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  };

  const messageStyle = {
    color: showOverlay ? "#ffffff" : "#6c757d",
    marginTop: "20px",
    fontSize: "16px",
    fontWeight: "500",
  };

  return (
    <div style={overlayStyle}>
      <FadeLoader color={"#FB1417"} height={15} width={5} />
      <p style={messageStyle}>{message}</p>
    </div>
  );
};

const IntakeForm = (props) => {
  const {
    intakeFormQuestions,
    painCategories,
    createIntakeFormQuestion,
    getUserIntakeFormQuestions,
    userIntakeFormQuestions,
    getUserIntakeCircuit,
    circuit,
    getPainCategories,
    getIntakeFormQuestions,
  } = props;

  const navigate = useNavigate();
  const hasInitRef = useRef(false);

  // ---------- Minimal state set ----------
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({}); // { [questionId]: answerId | [answerId,...] | text }
  const [viewMode, setViewMode] = useState(VIEW.QUESTIONS); // questions | body | pain
  const [selectedPainCategoryIds, setSelectedPainCategoryIds] = useState([]); // maintain chosen categories
  const [selectedPainPointIds, setSelectedPainPointIds] = useState([]); // chosen pain item ids

  // different state to keep track a single attempt pain select id
  const [choosenPainPoints, setChoosenPainPoints] = useState({
    attempt_1: 0,
    attempt_2: 0,
    attempt_3: 0,
  });

  const [highlightedImageIndex, setHighlightedImageIndex] = useState(null);

  const [frontPreview, setFrontPreview] = useState(front_side_1);
  const [backPreview, setBackPreview] = useState(back_side_1);
  const [showAnswerError, setShowAnswerError] = useState(false);
  const [isFront, setIsFront] = useState(true);

  const [painLibraryOpened, setPainLibraryOpened] = useState(false);

  const [previousAnswerContext, setPreviousAnswerContext] = useState(null); // Track previous answer selection for reset logic
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(true); // Track intakeFormQuestions loading state
  const [isSubmitting, setIsSubmitting] = useState(false); // Track createIntakeFormQuestion API loading state
  const [allPainAreasData, setAllPainAreasData] = useState([]);
  const [hasChangedAnswers, setHasChangedAnswers] = useState({}); // Track which questions have had answer changes since last API call
  const [otherTextInputs, setOtherTextInputs] = useState({}); // Store custom text for 'Other' options

  const currentQuestion = intakeFormQuestions?.[currentIndex];

  // a state that will keep track of the number of pain select attempts
  const [currentPainAreaSelectionCount, setCurrentPainAreaSelectionCount] =
    useState(0);

  // storing the details of a selected pain point
  const [selectedPainPointDetails, setSelectedPainPointDetails] =
    useState(null);

  // Find pain library from last selected category (simple behaviour)
  const activePainLibrary = useMemo(() => {
    const lastCatId =
      selectedPainCategoryIds[selectedPainCategoryIds.length - 1];
    const cat = painCategories?.find((c) => c.id === lastCatId);
    return cat?.painPointLibraries || [];
  }, [selectedPainCategoryIds, painCategories]);

  // Get progress summary
  const progressSummary = useMemo(() => {
    // Guard against non-array values (null/undefined/object) — prevents
    // `.filter is not a function` crash when store state is unexpected.
    if (!Array.isArray(intakeFormQuestions)) return { answered: 0, total: 0 };
    const answered = intakeFormQuestions.filter(
      (q) => answers[q.id] !== undefined
    ).length;
    return { answered, total: intakeFormQuestions.length };
  }, [intakeFormQuestions, answers]);

  // ---------- Derived helpers ----------
  const isYes = (str) => typeof str === "string" && str.toLowerCase() === "yes";

  // Helper to check if an answer is an 'Other' option
  const isOtherOption = (answerContent) => {
    return answerContent && answerContent.toLowerCase().includes("other");
  };

  // Handler for 'Other' text input changes
  const handleOtherTextChange = (questionId, answerId, value) => {
    const key = `${questionId}_${answerId}`;
    setOtherTextInputs((prev) => ({
      ...prev,
      [key]: value,
    }));

    // Mark this question as having changed answers
    setHasChangedAnswers((prevChanged) => ({
      ...prevChanged,
      [questionId]: true,
    }));
  };

  const toggleVisibility = () => {
    setIsFront(!isFront);
  };

  // Find if current question's selected answer is a YES that should trigger pain flow
  const currentSelectedAnswerObj = useMemo(() => {
    if (!currentQuestion) return null;
    const val = answers[currentQuestion.id];
    if (Array.isArray(val)) return null;
    return currentQuestion.answers?.find((a) => a.id === val) || null;
  }, [currentQuestion, answers]);

  // ---------- Init / data fetching ----------
  useEffect(() => {
    const init = async () => {
      setIsLoadingQuestions(true);
      await getUserIntakeCircuit();
      await getPainCategories();

      try {
        await getIntakeFormQuestions();
      } catch (error) {
        console.error("Error fetching intake form questions:", error);
      } finally {
        setIsLoadingQuestions(false);
      }
    };
    init();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (circuit) getUserIntakeFormQuestions(circuit);
    // eslint-disable-next-line
  }, [circuit]);

  // Helper function to process main question answers
  const processMainQuestionAnswers = useCallback(
    (item, answers, answeredQuestionIds, otherTextData) => {
      const questionId = item?.intakeFormItem?.parent_id;
      const answerId = item?.intakeFormItem?.id;

      if (!questionId || !answerId) return;

      // Guard against intakeFormQuestions being a non-array (e.g. null or
      // a paginated response object). Without this, `.find` would crash.
      if (!Array.isArray(intakeFormQuestions)) return;
      const question = intakeFormQuestions.find((q) => q.id === questionId);
      if (!question) return;

      if (question.type === "single-select") {
        answers[questionId] = answerId;
      } else {
        // Multi-select: accumulate answers in an array
        if (!answers[questionId]) answers[questionId] = [];
        if (!answers[questionId].includes(answerId)) {
          answers[questionId].push(answerId);
        }
      }

      // Restore otherText if it exists
      if (item.otherText) {
        const otherTextKey = `${questionId}_${answerId}`;
        otherTextData[otherTextKey] = item.otherText;
      }

      answeredQuestionIds.add(questionId);
    },
    [intakeFormQuestions]
  );

  // Helper function to process sub-question answers
  const processSubQuestionAnswers = (
    item,
    answers,
    painAreasData,
    otherTextData
  ) => {
    const {
      questionId,
      answerId,
      painPointId,
      painPointCategoryId,
      notes,
      answer_text,
      otherText,
    } = item;

    // Handle pain area sub-questions
    if (questionId && answerId && painPointId) {
      answers[questionId] = answerId;

      if (notes) {
        answers[`${questionId}_notes`] = notes;
      }

      // Restore otherText for sub-questions if it exists
      if (otherText) {
        const otherTextKey = `${questionId}_${answerId}`;
        otherTextData[otherTextKey] = otherText;
      }

      const painAreaEntry = {
        painPointCategoryId,
        painPointId,
        questionId,
        answerId,
        notes: notes || "",
      };

      // Include otherText in pain area data
      if (otherText) {
        painAreaEntry.otherText = otherText;
      }

      painAreasData.push(painAreaEntry);
    }

    // Handle text input sub-questions
    if (answer_text && questionId) {
      answers[questionId] = answer_text;
    }
  };

  // Helper function to restore pain areas state
  const restorePainAreasState = (painAreasData) => {
    if (painAreasData.length === 0) return;

    setAllPainAreasData(painAreasData);

    // Extract unique category and point IDs
    const categoryIds = [
      ...new Set(
        painAreasData.map((item) => item.painPointCategoryId).filter(Boolean)
      ),
    ];

    const pointIds = [
      ...new Set(painAreasData.map((item) => item.painPointId).filter(Boolean)),
    ];

    if (categoryIds.length > 0) {
      setSelectedPainCategoryIds(categoryIds);
    }
    if (pointIds.length > 0) {
      setSelectedPainPointIds(pointIds);
    }
  };

  // Helper function to calculate where user should resume
  const calculateResumePosition = useCallback(
    (answeredQuestionIds) => {
      // Guard against intakeFormQuestions being a non-array — prevents
      // `.findIndex` crash when state is in an unexpected shape.
      if (!Array.isArray(intakeFormQuestions)) return 0;
      const index = intakeFormQuestions.findIndex(
        (question) => !answeredQuestionIds.has(question.id)
      );

      // If all questions are answered, stay at the last one
      return index === -1 ? intakeFormQuestions.length - 1 : index;
    },
    [intakeFormQuestions]
  );

  // Populate previous answers and determine resume position
  useEffect(() => {
    // Early exits for missing data or already initialized
    if (!userIntakeFormQuestions?.length || !intakeFormQuestions?.length)
      return;
    if (hasInitRef.current) return;

    hasInitRef.current = true;

    const restoredAnswers = {};
    const answeredQuestionIds = new Set();
    const painAreasData = [];
    const otherTextData = {};

    // Process each user response
    userIntakeFormQuestions.forEach((item) => {
      processMainQuestionAnswers(
        item,
        restoredAnswers,
        answeredQuestionIds,
        otherTextData
      );
      processSubQuestionAnswers(
        item,
        restoredAnswers,
        painAreasData,
        otherTextData
      );
    });

    // Update component state
    setAnswers(restoredAnswers);
    setOtherTextInputs(otherTextData);
    restorePainAreasState(painAreasData);

    const resumeIndex = calculateResumePosition(answeredQuestionIds);
    setCurrentIndex(resumeIndex);
  }, [
    userIntakeFormQuestions,
    intakeFormQuestions,
    calculateResumePosition,
    processMainQuestionAnswers,
  ]);

  // ---------- Centralized answer handler (single place to mutate answers) ----------
  const handleAnswerChange = (questionId, answerId, isSingle, answerObj) => {
    // Check if this is a different answer selection for the same question

    const currentAnswerContext = `${questionId}_${answerId}`;

    const shouldResetSubQuestions =
      previousAnswerContext &&
      previousAnswerContext !== currentAnswerContext &&
      previousAnswerContext.startsWith(`${questionId}_`);

    setAnswers((prev) => {
      let newAnswer;
      if (isSingle) {
        newAnswer = { ...prev, [questionId]: answerId };
      } else {
        const prevArr = Array.isArray(prev[questionId]) ? prev[questionId] : [];
        const already = prevArr.includes(answerId);

        newAnswer = {
          ...prev,
          [questionId]: already
            ? prevArr.filter((id) => id !== answerId)
            : [...prevArr, answerId],
        };
      }

      // Mark this question as having changed answers since last API call
      setHasChangedAnswers((prevChanged) => ({
        ...prevChanged,
        [questionId]: true,
      }));

      return newAnswer;
    });

    // Clear other text if this answer is not 'Other'
    if (
      answerObj &&
      answerObj.content &&
      !answerObj.content.toLowerCase().includes("other")
    ) {
      setOtherTextInputs((prev) => {
        const newInputs = { ...prev };
        delete newInputs[`${questionId}_${answerId}`];
        return newInputs;
      });
    }

    // Reset subQuestion answers and notes only when switching to different answer within same question
    if (
      answerObj?.subQuestions &&
      answerObj.subQuestions.length > 0 &&
      shouldResetSubQuestions
    ) {
      resetSubQuestionAnswers(answerObj.subQuestions, true);
    }

    // Update the previous answer context
    setPreviousAnswerContext(currentAnswerContext);

    // if this answer is the 'Yes' pain-library trigger, open body view
    if (
      answerObj &&
      isYes(answerObj.content) &&
      answerObj.type === "pain-library"
    ) {
      setViewMode(VIEW.BODY);
      setPainLibraryOpened(true);

      // intialzing the very first pain attempt
      setCurrentPainAreaSelectionCount(1);
    } else {
      // if other answer selected -> make sure pain selections are cleared
      setSelectedPainCategoryIds([]);
      setSelectedPainPointIds([]);
      setHighlightedImageIndex(null);
      setPainLibraryOpened(false);
    }
  };

  const handleSubAnswerChange = (subQuestionId, value) => {
    setAnswers((prev) => ({ ...prev, [subQuestionId]: value }));
    // Mark the parent question as having changed answers when sub-questions change
    if (currentQuestion) {
      setHasChangedAnswers((prevChanged) => ({
        ...prevChanged,
        [currentQuestion.id]: true,
      }));
    }
  };

  const handleSubOptionChange = (subQuestionId, optionId, isSingle) => {
    setAnswers((prev) => {
      if (isSingle) {
        return { ...prev, [subQuestionId]: optionId };
      }

      const previous = Array.isArray(prev[subQuestionId])
        ? prev[subQuestionId]
        : [];
      const alreadySelected = previous.includes(optionId);
      return {
        ...prev,
        [subQuestionId]: alreadySelected
          ? previous.filter((id) => id !== optionId)
          : [...previous, optionId],
      };
    });

    // Mark the parent question as having changed answers when sub-questions change
    if (currentQuestion) {
      setHasChangedAnswers((prevChanged) => ({
        ...prevChanged,
        [currentQuestion.id]: true,
      }));
    }

    // hiding the body view
    setPainLibraryOpened(false);
  };

  // ---------- Pain / Body flow helpers (kept small) ----------
  const openPainLibraryForCategory = (categoryName) => {
    // added lower case to make sure the comparision is accurate
    const cat = painCategories?.find(
      (c) => c.name.toLowerCase() === categoryName.toLowerCase()
    );

    if (!cat) return;
    setSelectedPainCategoryIds((prev) =>
      Array.from(new Set([...prev, cat.id]))
    );

    setViewMode(VIEW.PAIN);
  };

  const onPainPointClick = (index, painPointId, currentAttempt) => {
    // scrolling to top for visual hiearchy
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });

    // storing the accurate pain id to the exact attempt it belongs
    setChoosenPainPoints((prev) => {
      prev[`attempt_${currentAttempt}`] = painPointId;

      return {
        ...prev,
      };
    });

    setHighlightedImageIndex(index);

    // Only clear sub-question answers if selecting a different pain point that doesn't have existing data
    if (currentSelectedAnswerObj?.subQuestions) {
      const hasExistingDataForThisPainPoint = allPainAreasData.some(
        (item) => item.painPointId === painPointId
      );

      if (!hasExistingDataForThisPainPoint) {
        // Only clear if this pain point doesn't have existing data
        setAnswers((prev) => {
          const newAnswers = { ...prev };
          currentSelectedAnswerObj.subQuestions.forEach((subQ) => {
            delete newAnswers[subQ.id];
            delete newAnswers[`${subQ.id}_notes`];
          });
          return newAnswers;
        });
      } else {
        // Restore existing data for this pain point
        const existingDataForPainPoint = allPainAreasData.filter(
          (item) => item.painPointId === painPointId
        );

        setAnswers((prev) => {
          const newAnswers = { ...prev };
          existingDataForPainPoint.forEach((item) => {
            newAnswers[item.questionId] = item.answerId;
            if (item.notes) {
              newAnswers[`${item.questionId}_notes`] = item.notes;
            }
          });
          return newAnswers;
        });

        // Restore other text inputs for this pain point
        setOtherTextInputs((prev) => {
          const newOtherInputs = { ...prev };
          existingDataForPainPoint.forEach((item) => {
            if (item.otherText) {
              const otherTextKey = `${item.questionId}_${item.answerId}`;
              newOtherInputs[otherTextKey] = item.otherText;
            }
          });
          return newOtherInputs;
        });
      }
    }

    setViewMode(VIEW.SUB_QUESTIONS);
  };

  // assinging a use effect to observe the choosen points so that it can be assigned to the same varaible to avoid any extra hassle
  useEffect(() => {
    // if the pain method started then start observing it

    if (currentPainAreaSelectionCount > 0) {
      // only getting the pain values that has been selected
      const values = Object.values(choosenPainPoints).slice(
        0,
        currentPainAreaSelectionCount
      );

      setSelectedPainPointIds([...values]);
    }
  }, [choosenPainPoints, currentPainAreaSelectionCount]);

  const resetPainState = () => {
    setSelectedPainCategoryIds([]);
    setSelectedPainPointIds([]);
    setHighlightedImageIndex(null);
    setViewMode(VIEW.QUESTIONS);

    setAllPainAreasData([]); // Clear all stored pain areas data
  };

  // fetching additional info from local json to help styling and rendering conditionally
  const { data: additonal_info, isLoading: additional_info_loading } =
    useGetQuestionInfo(currentQuestion?.content);

  // checking if the current questions has subQuestions
  const [subQuestionCount, setSubQuestionCount] = useState(null);
  const [activeSubQuestionIndex, setActiveSubQuestionIndex] = useState(null);
  const [subQuestionAnswered, setSubQuestionAnswered] = useState(0);

  useEffect(() => {
    if (additonal_info?.sub_questions) {
      setSubQuestionCount(additonal_info.sub_questions);
      setActiveSubQuestionIndex(0);
    } else {
      setSubQuestionCount(null);
      setActiveSubQuestionIndex(null);
    }
  }, [additonal_info]);

  // ---------- Formatting payload similar to original formatResponseData ----------
  const formatResponseData = () => {
    const formatted = {};
    Object.keys(answers).forEach((qId) => {
      const q = intakeFormQuestions?.find(
        (qq) => String(qq.id) === String(qId)
      );
      if (!q) return;
      const val = answers[qId];
      const item = { answer_id: val, subQuestion: [] };
      if (!Array.isArray(val)) {
        const selectedAnswer = q.answers?.find((a) => a.id === val);
        if (selectedAnswer?.subQuestions?.length) {
          selectedAnswer.subQuestions.forEach((subQ) => {
            if (answers[subQ.id] !== undefined) {
              item.subQuestion.push({
                id: subQ.id,
                content: subQ.content,
                answer: answers[subQ.id],
                notes: subQ.notes || answers[`${subQ.id}_notes`] || "",
              });
            }
          });
        }
      }
      formatted[qId] = item;
    });

    return formatted;
  };

  const handleYesAddAnother = async () => {
    // resetting the sub question index
    setActiveSubQuestionIndex(0);

    setViewMode(VIEW.BODY);

    setPainLibraryOpened(true);

    // increasing the pain attempt
    setCurrentPainAreaSelectionCount((prev) => prev + 1);

    // resetting the previous pain point details
    setSelectedPainPointDetails(null);
  };

  // ---------- Navigation (next/prev) ----------
  const handleNext = async (showPainConfirmation = true) => {
    if (!answers[currentQuestion?.id]) {
      setShowAnswerError(true);
      return;
    }
    setShowAnswerError(false);

    // Save current pain area's sub-questions in the required flat format
    const currentPainPointId =
      selectedPainPointIds[selectedPainPointIds.length - 1];

    const currentPainPointCategoryId =
      selectedPainCategoryIds[selectedPainCategoryIds.length - 1];

    let updatedPainAreasData = allPainAreasData;

    if (currentSelectedAnswerObj?.subQuestions) {
      const newEntries = [];
      currentSelectedAnswerObj.subQuestions.forEach((subQ) => {
        if (answers[subQ.id] !== undefined) {
          const entry = {
            painPointCategoryId: currentPainPointCategoryId,
            painPointId: currentPainPointId,
            questionId: subQ.id,
            answerId: answers[subQ.id],
            notes: answers[`${subQ.id}_notes`] || "",
          };

          // Check if the sub-question answer(s) include 'Other' and add custom text
          const answerIds = Array.isArray(answers[subQ.id])
            ? answers[subQ.id]
            : [answers[subQ.id]];

          let otherTextForSubQ = "";
          answerIds.forEach((answerId) => {
            const answerObj = subQ.subAnswers?.find((a) => a.id === answerId);
            if (answerObj && isOtherOption(answerObj.content)) {
              const otherTextKey = `${subQ.id}_${answerId}`;
              const otherTextValue = otherTextInputs[otherTextKey] || "";
              if (otherTextValue.trim()) {
                otherTextForSubQ +=
                  (otherTextForSubQ ? "; " : "") + otherTextValue;
              }
            }
          });

          if (otherTextForSubQ) {
            entry.otherText = otherTextForSubQ;
          }

          newEntries.push(entry);
        }
      });

      // Calculate updated pain areas data
      updatedPainAreasData = [
        ...allPainAreasData.filter(
          (item) => item.painPointId !== currentPainPointId
        ),
        ...newEntries,
      ];

      // Update state
      setAllPainAreasData(updatedPainAreasData);

      // Mark that pain areas have changed for this question
      if (currentQuestion) {
        setHasChangedAnswers((prevChanged) => ({
          ...prevChanged,
          [currentQuestion.id]: true,
        }));
      }
    }

    const selected = currentSelectedAnswerObj;

    if (
      selected?.type === "pain-library" &&
      showPainConfirmation &&
      currentPainAreaSelectionCount <= 3 &&
      subQuestionAnswered < 3 &&
      !(
        subQuestionAnswered === 2 &&
        activeSubQuestionIndex + 1 === subQuestionCount
      )
    ) {
      // preventing going into next steps and showing all the sub questions first
      if (
        subQuestionCount &&
        activeSubQuestionIndex + 1 !== subQuestionCount &&
        subQuestionAnswered <= 2
      ) {
        setActiveSubQuestionIndex((prev) => prev + 1);
        return;
      }

      // resetting the subquestion index
      setActiveSubQuestionIndex(0);
      // defining how many questions with subquestions has been answered
      setSubQuestionAnswered((prev) => prev + 1);

      if (currentPainAreaSelectionCount < 3) {
        setViewMode(VIEW.PAIN_CONFIRMATION);
      }
      return;
    }

    // Validate current sub-questions
    if (
      selected &&
      isYes(selected.content) &&
      selected.subQuestions?.length > 0
    ) {
      for (const subQ of selected.subQuestions) {
        if (String(answers[subQ.id] ?? "").trim() === "") return;
      }
    }

    // OPTIMIZATION: Check if the current question's answer has actually changed
    // Skip API call if user just navigated back and forward without changing answers
    // This prevents unnecessary API calls when users are just reviewing their answers
    const questionHasChanges = hasChangedAnswers[currentQuestion?.id];

    // Always make API call on the last question (submit) or if answers have changed
    const isLastQuestion = currentIndex === intakeFormQuestions?.length - 1;
    const shouldMakeApiCall = isLastQuestion || questionHasChanges;

    if (shouldMakeApiCall) {
      // resetting the pain point details if completely moves to next step
      setSelectedPainPointDetails(null);

      const payload = {
        question_id: currentQuestion?.id,
        answer_id: formatResponseData()[currentQuestion?.id]?.answer_id,
        circuit,
      };

      if (updatedPainAreasData.length > 0) {
        // Add all pain areas data (use updated data to ensure current pain area is included)
        payload.allPainAreasData = updatedPainAreasData;
      }

      // Check if current answer(s) include 'Other' and add custom text
      const currentAnswer = answers[currentQuestion?.id];
      if (currentAnswer) {
        const answerIds = Array.isArray(currentAnswer)
          ? currentAnswer
          : [currentAnswer];
        const otherTexts = {};
        let hasOtherText = false;

        answerIds.forEach((answerId) => {
          const answerObj = currentQuestion?.answers?.find(
            (a) => a.id === answerId
          );
          if (answerObj && isOtherOption(answerObj.content)) {
            const otherTextKey = `${currentQuestion?.id}_${answerId}`;
            const otherTextValue = otherTextInputs[otherTextKey] || "";
            if (otherTextValue.trim()) {
              otherTexts[answerId] = otherTextValue;
              hasOtherText = true;
            }
          }
        });

        if (hasOtherText) {
          payload.otherText = otherTexts;
        }
      }

      const existing = userIntakeFormQuestions?.find(
        (it) => it?.intakeFormItem?.parent_id === currentQuestion?.id
      );
      if (existing) payload.id = existing.id;

      payload.isCircuitComplete = isLastQuestion ? 1 : 0;

      setIsSubmitting(true);
      try {
        await createIntakeFormQuestion(payload);

        if (payload.isCircuitComplete) {
          setAnswers({});
          resetPainState();
          setAllPainAreasData([]); // Clear all pain areas data
          setHasChangedAnswers({}); // Clear change tracking
          setOtherTextInputs({}); // Clear other text inputs
          navigate("/user-assessments");
          return;
        }

        // Clear the change flag for this question after successful API call
        // This allows skipping API calls if user navigates back/forward without changes
        setHasChangedAnswers((prev) => ({
          ...prev,
          [currentQuestion?.id]: false,
        }));
      } catch (error) {
        console.error("Error submitting intake form:", error);
        // You might want to show an error message to the user here
      } finally {
        setIsSubmitting(false);
      }
    }

    if (currentIndex < intakeFormQuestions?.length - 1) {
      const nextIndex = currentIndex + 1;
      setViewMode(VIEW.QUESTIONS);
      setCurrentIndex(nextIndex);
      const nextQuestion = intakeFormQuestions?.[nextIndex];
      if (nextQuestion) {
        setPreviousAnswerContext(`${nextQuestion.id}_init`);
      }

      // restting all the sub question related state
      setSubQuestionAnswered(0);
      setActiveSubQuestionIndex(null);

      // scrolling top to next question
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else navigate("/user-assessments");
  };

  const handleBack = () => {
    if (viewMode === VIEW.SUB_QUESTIONS) {
      // since the user is still in body view so fixing the state

      setPainLibraryOpened(true);

      // Save current sub-question state before navigating back
      const currentPainPointId =
        selectedPainPointIds[selectedPainPointIds.length - 1];
      const currentPainPointCategoryId =
        selectedPainCategoryIds[selectedPainCategoryIds.length - 1];

      if (currentSelectedAnswerObj?.subQuestions && currentPainPointId) {
        const newEntries = [];
        currentSelectedAnswerObj.subQuestions.forEach((subQ) => {
          if (answers[subQ.id] !== undefined) {
            const entry = {
              painPointCategoryId: currentPainPointCategoryId,
              painPointId: currentPainPointId,
              questionId: subQ.id,
              answerId: answers[subQ.id],
              notes: answers[`${subQ.id}_notes`] || "",
            };

            // Check if the sub-question answer(s) include 'Other' and add custom text
            const answerIds = Array.isArray(answers[subQ.id])
              ? answers[subQ.id]
              : [answers[subQ.id]];

            let otherTextForSubQ = "";
            answerIds.forEach((answerId) => {
              const answerObj = subQ.subAnswers?.find((a) => a.id === answerId);
              if (answerObj && isOtherOption(answerObj.content)) {
                const otherTextKey = `${subQ.id}_${answerId}`;
                const otherTextValue = otherTextInputs[otherTextKey] || "";
                if (otherTextValue.trim()) {
                  otherTextForSubQ +=
                    (otherTextForSubQ ? "; " : "") + otherTextValue;
                }
              }
            });

            if (otherTextForSubQ) {
              entry.otherText = otherTextForSubQ;
            }

            newEntries.push(entry);
          }
        });

        // Update pain areas data with current entries
        setAllPainAreasData((prev) => [
          ...prev.filter((item) => item.painPointId !== currentPainPointId),
          ...newEntries,
        ]);

        // Mark that pain areas have changed for this question
        if (currentQuestion) {
          setHasChangedAnswers((prevChanged) => ({
            ...prevChanged,
            [currentQuestion.id]: true,
          }));
        }
      }

      // Check if we have selected pain areas to show the pain library
      if (selectedPainPointIds.length > 0) {
        // Restore the highlighted index for the current pain point when going back to pain library
        const currentPainPointId =
          selectedPainPointIds[selectedPainPointIds.length - 1];
        const currentPainPointIndex = activePainLibrary.findIndex(
          (point) => point.id === currentPainPointId
        );
        if (currentPainPointIndex !== -1) {
          setHighlightedImageIndex(currentPainPointIndex);
        }
        return setViewMode(VIEW.PAIN);
      } else {
        // If no pain areas selected, go to body view
        return setViewMode(VIEW.BODY);
      }
    }
    // If in pain view, go back to body
    if (viewMode === VIEW.PAIN) return setViewMode(VIEW.BODY);

    // if in body view, go back to questions
    if (viewMode === VIEW.BODY) {
      setViewMode(VIEW.QUESTIONS);

      //  making sure that when user leaves body view mode, it triggers the next button back to normal state
      setPainLibraryOpened(false);
      return;
    }

    if (currentIndex > 0) {
      const prevIndex = currentIndex - 1;
      const prevQ = intakeFormQuestions?.[prevIndex];

      // Ensure we go back to questions view
      setViewMode(VIEW.QUESTIONS);

      // restore user's stored answer if available (answers are already loaded)
      setCurrentIndex(prevIndex);

      // Update context for the previous question
      if (prevQ) {
        setPreviousAnswerContext(`${prevQ.id}_init`);
      }
    }
  };

  // Handle subQuestion note changes
  // Reset subQuestion selections and notes only when switching to different answer options
  const resetSubQuestionAnswers = (subQuestions, forceReset = false) => {
    if (!subQuestions || !Array.isArray(subQuestions)) return;

    // Only reset if explicitly forced (when changing answer options within same question)
    if (!forceReset) return;

    setAnswers((prev) => {
      const newAnswers = { ...prev };
      subQuestions.forEach((subQ) => {
        // Reset the answer
        delete newAnswers[subQ.id];
        // Reset the notes
        delete newAnswers[`${subQ.id}_notes`];
        // Reset the notes property on the subQuestion object
        if (subQ.notes) {
          subQ.notes = "";
        }
      });
      return newAnswers;
    });
  };

  // calculating the dynamic header height on mobile devices for viewport content visibility
  const headerRef = useRef();
  const contentRef = useRef();
  const [mobileHeaderHeight, setMobileHeaderHeight] = useState(null);

  useEffect(() => {
    if (!headerRef.current) return;

    if (!additonal_info) return;

    const calculateHeight = () => {
      if (window.matchMedia("(max-width: 767px)").matches) {
        const headerHeight = headerRef.current.clientHeight;
        setMobileHeaderHeight(headerHeight);
      } else {
        setMobileHeaderHeight(null);
      }
    };

    // Calculate immediately
    calculateHeight();

    // Recalculate when window resizes
    window.addEventListener("resize", calculateHeight);

    return () => {
      window.removeEventListener("resize", calculateHeight);
    };
  }, [additonal_info]);

  useEffect(() => {
    if (!contentRef.current) return;

    if (!mobileHeaderHeight) return;

    contentRef.current.style.minHeight = `calc( 100vh - ${mobileHeaderHeight}px)`;
  }, [mobileHeaderHeight]);

  // Show loading spinner while intakeFormQuestions is being fetched
  if (isLoadingQuestions || additional_info_loading) {
    return (
      <>
        <ScrollTopOnMount />
        <CommonPageData>
          <section className="recomended-products">
            <div className="common-banner-block intake_form_banner_block">
              <div className="main-banner">
                <img src="/images/banner-common-block.png" alt="img" />
              </div>
              <div className="container">
                <div className="common-banner-block-inner">
                  <div className="intake_form_header">
                    <p className="bold">Intake Form</p>
                  </div>
                </div>
              </div>

              {/* header logo */}
              <HeaderLogo />
            </div>
            <div className="container intake_form_loader_block">
              <div className="row justify-content-center">
                <div className="col-12 col-lg-6 col-md-6 m-auto">
                  <div
                    className="loading-container"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "60px 20px",
                      minHeight: "300px",
                    }}
                  >
                    <FadeLoader color={"#FB1417"} height={15} width={5} />
                    <p
                      className="mt-3 text-muted"
                      style={{
                        fontSize: "16px",
                        fontWeight: "500",
                        marginTop: "20px",
                      }}
                    >
                      Loading intake form questions...
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </CommonPageData>
        <Footer />
      </>
    );
  }

  // ---------- Render ----------
  return (
    <>
      <ScrollTopOnMount />
      <CommonPageData>
        <section className="recomended-products">
          <div
            ref={headerRef}
            className="common-banner-block intake_form_banner_block"
          >
            <div className="main-banner">
              <img src="/images/banner-common-block.png" alt="img" />
            </div>
            <div className="container">
              <div className="common-banner-block-inner">
                <div className="intake_form_header">
                  {selectedPainPointDetails &&
                  viewMode !== VIEW.PAIN_CONFIRMATION &&
                  viewMode !== VIEW.BODY &&
                  viewMode !== VIEW.PAIN ? (
                    <>
                      <p className="bold">
                        Pain Area {`#${currentPainAreaSelectionCount}`}{" "}
                      </p>
                      <p> - </p>
                      <p className="plain">
                        {
                          additonal_info?.sub_questions_dynamic_title[
                            activeSubQuestionIndex
                          ]
                        }
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="bold">Intake Form</p>
                      <p> - </p>
                      <p className="plain">
                        {selectedPainPointDetails &&
                        viewMode === VIEW.PAIN_CONFIRMATION
                          ? `Add pain area`
                          : additonal_info?.dynamic_title}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* header logo */}
            <HeaderLogo />
          </div>

          {additonal_info?.instruction_text &&
            additonal_info?.instruction_text_position === "top" && (
              <div className="question_instruction_text">
                <p>{additonal_info.instruction_text} </p>
              </div>
            )}

          {intakeFormQuestions?.length > 0 ? (
            <div
              ref={contentRef}
              style={{
                minHeight: `${mobileHeaderHeight ? `` : ``}`,
              }}
              className={`intake-form-page form_content ${
                viewMode === VIEW.BODY || viewMode === VIEW.PAIN
                  ? `no_padding`
                  : ``
              }${
                additonal_info?.instruction_text &&
                currentQuestion.order_no === 1
                  ? `instruction_text_visible`
                  : ``
              } `}
            >
              <div className="container">
                <div className="intake-form-wrapper">
                  <div className="modal-body-custom">
                    {currentQuestion && (
                      <div className="step-form-main">
                        {/* Progress indicators */}

                        {/* hiding the indicator if any any kind of pain view or select mode is turned on */}
                        {!(
                          viewMode === VIEW.BODY ||
                          viewMode === VIEW.PAIN ||
                          viewMode === VIEW.PAIN_CONFIRMATION ||
                          selectedPainPointDetails
                        ) &&
                          progressSummary.total > 0 && (
                            <div className="progress_bar_wrapper">
                              {currentQuestion?.order_no > 2 && (
                                <p className="intake_form_instruction_text upper">
                                  You'll move on to your movement assessment
                                  after completing this form.
                                </p>
                              )}
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "end",
                                  alignItems: "center",
                                  marginBottom: "10px",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "14px",
                                    color: "#666",
                                  }}
                                >
                                  {Math.round(
                                    (progressSummary.answered /
                                      progressSummary.total) *
                                      100
                                  )}
                                  % Intake Form complete
                                </span>
                              </div>

                              <div className="progress_bar_indicator">
                                <div
                                  style={{
                                    width: `${
                                      (progressSummary.answered /
                                        progressSummary.total) *
                                      100
                                    }%`,
                                    height: "100%",
                                    backgroundColor: "#c00000",
                                    transition: "width 0.3s ease",
                                  }}
                                ></div>
                              </div>

                              {currentQuestion?.order_no <= 2 && (
                                <p className="intake_form_instruction_text bottom">
                                  You'll move on to your movement assessment
                                  after completing this form.
                                </p>
                              )}
                            </div>
                          )}

                        <div
                          className={`step-number-block ${
                            additonal_info?.content_position === "center"
                              ? `center_align`
                              : ``
                          }  ${
                            viewMode === VIEW.BODY ? `bottom_no_padding` : ``
                          } `}
                          key={currentQuestion.id}
                        >
                          <>
                            {viewMode === VIEW.QUESTIONS && (
                              <>
                                {additonal_info?.instruction_text &&
                                  additonal_info?.instruction_text_position ===
                                    "center" && (
                                    <div className="question_instruction_text center">
                                      <p>{additonal_info.instruction_text}</p>
                                    </div>
                                  )}
                                <h4 className="intake_question_title">
                                  {currentQuestion.content}{" "}
                                  {currentQuestion.type === "single-select" ? (
                                    <span className="sub_text">
                                      {additonal_info?.question_count ===
                                      "binary"
                                        ? ``
                                        : `Select Only One`}
                                    </span>
                                  ) : (
                                    <span className="sub_text">
                                      Select All That Apply
                                    </span>
                                  )}
                                </h4>
                                <div
                                  className={`check-box-block-modal ${
                                    currentQuestion.answers.length > 3
                                      ? "check-box-block-modal-three"
                                      : ""
                                  }`}
                                >
                                  {currentQuestion.answers.map((answer) => {
                                    const isSingle =
                                      currentQuestion.type === "single-select";
                                    const inputType = isSingle
                                      ? "radio"
                                      : "checkbox";
                                    const isChecked = isSingle
                                      ? answers[currentQuestion.id] ===
                                        answer.id
                                      : Array.isArray(
                                          answers[currentQuestion.id]
                                        ) &&
                                        answers[currentQuestion.id].includes(
                                          answer.id
                                        );

                                    return (
                                      <div key={answer.id}>
                                        <div className="check_test">
                                          <input
                                            id={`q${currentQuestion.id}_a${answer.id}`}
                                            type={inputType}
                                            name={`q_${currentQuestion.id}`}
                                            checked={!!isChecked}
                                            onChange={() =>
                                              handleAnswerChange(
                                                currentQuestion.id,
                                                answer.id,
                                                isSingle,
                                                answer
                                              )
                                            }
                                          />
                                          <label
                                            htmlFor={`q${currentQuestion.id}_a${answer.id}`}
                                          >
                                            <span class="box"></span>
                                            <span>{answer.content}</span>
                                          </label>
                                        </div>
                                        {/* Show text input for 'Other' option when selected */}
                                        {isOtherOption(answer.content) &&
                                          isChecked && (
                                            <div
                                              className="mt-4 mb-3"
                                              style={{ marginLeft: "30px" }}
                                            >
                                              <input
                                                type="text"
                                                className="form-control other_input_field"
                                                placeholder="If Other, please specify…"
                                                value={
                                                  otherTextInputs[
                                                    `${currentQuestion.id}_${answer.id}`
                                                  ] || ""
                                                }
                                                onChange={(e) =>
                                                  handleOtherTextChange(
                                                    currentQuestion.id,
                                                    answer.id,
                                                    e.target.value
                                                  )
                                                }
                                                style={{
                                                  maxWidth: "300px",
                                                  border: "none",
                                                  borderBottom: "1px solid",
                                                  borderBottomColor: "#cdcdcd",
                                                  padding: "8px 12px",
                                                  borderRadius: "0px",
                                                }}
                                              />
                                            </div>
                                          )}
                                      </div>
                                    );
                                  })}
                                </div>
                                {showAnswerError && (
                                  <div className="text-danger">
                                    Please select an answer before continuing.
                                  </div>
                                )}
                              </>
                            )}
                            {/* Sub-questions (render when appropriate) */}
                            {viewMode === VIEW.SUB_QUESTIONS && (
                              <div
                                className={` ${
                                  selectedPainPointDetails
                                    ? `pain_point_details_block`
                                    : ``
                                } `}
                              >
                                {selectedPainPointDetails && (
                                  <div class="details">
                                    <div class="img_holder">
                                      <img
                                        src={selectedPainPointDetails?.image}
                                        alt=""
                                      />
                                    </div>

                                    <p className="title">
                                      {" "}
                                      {selectedPainPointDetails?.title}{" "}
                                    </p>
                                  </div>
                                )}

                                <div className="yes-after-block">
                                  {currentSelectedAnswerObj?.subQuestions?.map(
                                    (subQ, index) => {
                                      // only showing the current active subquestion
                                      if (index !== activeSubQuestionIndex)
                                        return "";

                                      const hasOptions =
                                        Array.isArray(subQ.subAnswers) &&
                                        subQ.subAnswers.length > 0;
                                      const isSingle =
                                        subQ.type === "single-select";
                                      const inputType = isSingle
                                        ? "radio"
                                        : "checkbox";

                                      return (
                                        <div key={subQ.id} className="mb-4">
                                          <h4 className="mb-2 intake_question_title sub_question">
                                            {subQ.content}
                                            {isSingle ? (
                                              <span className="sub_text">
                                                Select Only One
                                              </span>
                                            ) : (
                                              <span className="sub_text">
                                                Select All That Apply
                                              </span>
                                            )}
                                          </h4>
                                          {hasOptions ? (
                                            <div
                                              className={`check-box-block-modal`}
                                            >
                                              {subQ.subAnswers.map((opt) => {
                                                const isChecked = isSingle
                                                  ? answers[subQ.id] === opt.id
                                                  : Array.isArray(
                                                      answers[subQ.id]
                                                    ) &&
                                                    answers[subQ.id].includes(
                                                      opt.id
                                                    );
                                                return (
                                                  <div key={opt.id}>
                                                    <div className="check_test">
                                                      <input
                                                        id={`sq${subQ.id}_opt${opt.id}`}
                                                        type={inputType}
                                                        name={`sq_${subQ.id}`}
                                                        checked={!!isChecked}
                                                        onChange={() =>
                                                          handleSubOptionChange(
                                                            subQ.id,
                                                            opt.id,
                                                            isSingle
                                                          )
                                                        }
                                                      />
                                                      <label
                                                        htmlFor={`sq${subQ.id}_opt${opt.id}`}
                                                      >
                                                        <span class="box"></span>
                                                        <span>
                                                          {opt.content}
                                                        </span>
                                                      </label>
                                                    </div>
                                                    {/* Show text input for 'Other' option when selected */}
                                                    {isOtherOption(
                                                      opt.content
                                                    ) &&
                                                      isChecked && (
                                                        <div
                                                          className="mt-2 mb-3 other_input_field"
                                                          style={{
                                                            marginLeft: "30px",
                                                          }}
                                                        >
                                                          <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="If Other, please specify…"
                                                            value={
                                                              otherTextInputs[
                                                                `${subQ.id}_${opt.id}`
                                                              ] || ""
                                                            }
                                                            onChange={(e) =>
                                                              handleOtherTextChange(
                                                                subQ.id,
                                                                opt.id,
                                                                e.target.value
                                                              )
                                                            }
                                                            style={{
                                                              maxWidth: "300px",
                                                              border: "none",
                                                              borderBottom:
                                                                "1px solid",
                                                              borderBottomColor:
                                                                "#cdcdcd",
                                                              padding:
                                                                "8px 12px",
                                                              borderRadius:
                                                                "0px",
                                                            }}
                                                          />
                                                        </div>
                                                      )}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          ) : (
                                            <input
                                              className="form-control"
                                              value={answers[subQ.id] || ""}
                                              onChange={(e) =>
                                                handleSubAnswerChange(
                                                  subQ.id,
                                                  e.target.value
                                                )
                                              }
                                            />
                                          )}
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                              </div>
                            )}
                            {/* Triggered body/pain flow */}
                            <div className="yes-after-block">
                              {viewMode === VIEW.BODY && (
                                <>
                                  <div className="yes-after-block-step">
                                    <div className="human-body-container">
                                      <div className="human-body-container-inner">
                                        <h4 className="body_view_instruction_title">
                                          Step 1. Select Pain Area​{" "}
                                          <span className="sub_text">
                                            (Up to 3 areas—most painful first)
                                          </span>
                                        </h4>
                                      </div>
                                      {/* <div className="human-body-container-inner d-flex align-items-start"> */}
                                      <div className="human-body-container-inner body_view flex">
                                        <BodyViewer
                                          isFront={isFront}
                                          frontPreview={frontPreview}
                                          backPreview={backPreview}
                                          onClickBodyParts={
                                            openPainLibraryForCategory
                                          }
                                          handleMouseEnter={(f, b) => {
                                            setFrontPreview(f);
                                            setBackPreview(b);
                                          }}
                                          handleMouseLeave={() => {
                                            setFrontPreview(front_side_1);
                                            setBackPreview(back_side_1);
                                          }}
                                        />
                                        <button
                                          onClick={toggleVisibility}
                                          className="float-end ms-3 toggle_body_view_button"
                                        >
                                          <svg
                                            width="80"
                                            height="80"
                                            viewBox="0 0 80 80"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <circle
                                              cx="40"
                                              cy="40"
                                              r="40"
                                              fill="#C00000"
                                            />
                                            <path
                                              d="M53.3838 25.884C53.6181 25.6496 53.7497 25.3317 53.7497 25.0003C53.7497 24.6688 53.6181 24.3509 53.3838 24.1165L45.8838 16.6165C45.648 16.3888 45.3322 16.2628 45.0045 16.2657C44.6768 16.2685 44.3632 16.4 44.1315 16.6317C43.8997 16.8635 43.7683 17.177 43.7654 17.5048C43.7626 17.8325 43.8886 18.1483 44.1163 18.384L49.4825 23.7503H27.5C27.1685 23.7503 26.8505 23.882 26.6161 24.1164C26.3817 24.3508 26.25 24.6687 26.25 25.0003C26.25 25.3318 26.3817 25.6497 26.6161 25.8841C26.8505 26.1186 27.1685 26.2503 27.5 26.2503H49.4825L44.1163 31.6165C43.8886 31.8523 43.7626 32.168 43.7654 32.4958C43.7683 32.8235 43.8997 33.137 44.1315 33.3688C44.3632 33.6005 44.6768 33.732 45.0045 33.7349C45.3322 33.7377 45.648 33.6117 45.8838 33.384L53.3838 25.884Z"
                                              fill="white"
                                            />
                                            <path
                                              d="M26.6162 55.884C26.3819 55.6496 26.2503 55.3317 26.2503 55.0003C26.2503 54.6688 26.3819 54.3509 26.6162 54.1165L34.1162 46.6165C34.352 46.3888 34.6678 46.2628 34.9955 46.2657C35.3232 46.2685 35.6368 46.4 35.8685 46.6317C36.1003 46.8635 36.2317 47.177 36.2346 47.5048C36.2374 47.8325 36.1114 48.1483 35.8837 48.384L30.5175 53.7503H52.5C52.8315 53.7503 53.1495 53.882 53.3839 54.1164C53.6183 54.3508 53.75 54.6687 53.75 55.0003C53.75 55.3318 53.6183 55.6497 53.3839 55.8841C53.1495 56.1186 52.8315 56.2503 52.5 56.2503H30.5175L35.8837 61.6165C36.1114 61.8523 36.2374 62.168 36.2346 62.4958C36.2317 62.8235 36.1003 63.137 35.8685 63.3688C35.6368 63.6005 35.3232 63.732 34.9955 63.7349C34.6678 63.7377 34.352 63.6117 34.1162 63.384L26.6162 55.884Z"
                                              fill="white"
                                            />
                                          </svg>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </>
                              )}

                              {viewMode === VIEW.PAIN && (
                                <>
                                  <PainArea
                                    library={activePainLibrary}
                                    highlightedImageIndex={
                                      highlightedImageIndex
                                    }
                                    onSelectPainPoint={onPainPointClick}
                                    selectedPainPointIds={selectedPainPointIds}
                                    currentPainAreaSelectionCount={
                                      currentPainAreaSelectionCount
                                    }
                                    setSelectedPainPointDetails={
                                      setSelectedPainPointDetails
                                    }
                                  />
                                </>
                              )}

                              {viewMode === VIEW.PAIN_CONFIRMATION && (
                                <PainConfirmation
                                  maxAttempts={3}
                                  currentAttempts={
                                    currentPainAreaSelectionCount
                                  }
                                  onYesAddAnother={handleYesAddAnother}
                                  onNoAddAnother={() => {
                                    handleNext(false);

                                    // resetting the previous pain point details
                                    setSelectedPainPointDetails(null);
                                  }}
                                  onFinish={handleNext}
                                />
                              )}
                            </div>
                          </>

                          {/* If viewMode is body but question isn't present - show nothing; flow is driven by viewMode */}
                        </div>
                      </div>
                    )}

                    {additonal_info?.has_note_box && (
                      <div className="prev_pain_note_box">
                        <textarea
                          placeholder="If you selected any area above, please explain as fully as you can for each region or side.  More details help us connect past 
injuries to current pain.




 (i.e., LEFT high ankle sprain 2021,
 RIGHT recurring medial shin pain.  
LEFT medial knee pain 2024)
"
                        ></textarea>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {viewMode !== VIEW.PAIN_CONFIRMATION && (
                <div className="step-footer-block">
                  <div className="back-next-btn">
                    <button
                      className="back-btn"
                      onClick={handleBack}
                      disabled={currentIndex === 0}
                    >
                      Back
                    </button>
                  </div>
                  <div className="back-next-btn">
                    <button
                      className="common-btn"
                      onClick={handleNext}
                      disabled={isSubmitting || painLibraryOpened}
                    >
                      {currentIndex === intakeFormQuestions?.length - 1
                        ? "Submit"
                        : "Next"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center m-5">
              <img
                src={notFound}
                alt="No questions"
                style={{
                  width: "250px",
                  marginBottom: "20px",
                  opacity: 0.7,
                }}
              />
              <h5 className="text-muted">No questions found</h5>
            </div>
          )}
        </section>
      </CommonPageData>

      <Footer />

      {/* Full-screen loader overlay for API submission */}
      <FullScreenLoader
        isVisible={isSubmitting}
        message="Submitting your response..."
        showOverlay={true}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  userIntakeFormQuestions: state.assessmentReducer.userIntakeFormQuestions,
  circuit: state.assessmentReducer.circuit,
  intakeFormQuestions: state.assessmentReducer.intakeFormQuestions,
  painCategories: state.programReducer.musclesCategories,
});

export default connect(mapStateToProps, {
  createIntakeFormQuestion,
  getUserIntakeFormQuestions,
  getUserIntakeCircuit,
  getPainCategories,
  getIntakeFormQuestions,
})(IntakeForm);
